import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { assetKeys } from './assets.query';

export type JobKind = 'bulk' | 'signal' | 'revert';
export type JobStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
export type JobScope = { asids: number[] } | { filter: Record<string, string> };

export type AssetJob = {
  ajid: number;
  kind: JobKind;
  action: string;
  params: Record<string, any> | null;
  scope: Record<string, any> | null;
  reason: string;
  status: JobStatus;
  env: string | null;
  created_by: number | null;
  total: number;
  done: number;
  skipped: number;
  failed: number;
  pending: number;
  paid_calls: number;
  reverts_ajid: number | null;
  reverted_by: number | null;
  error: string | null;
  date_added: string | null;
  started_at: string | null;
  finished_at: string | null;
};

export type JobPlan = {
  dry_run?: boolean;
  kind: JobKind;
  action: string;
  matched: number;
  total: number;
  skipped?: number;
  listings: number;
  seller_notices?: number;
  paid_calls: number;
  cost_estimate_usd?: number | null;
  paid_cap?: number;
  paid_used_today?: number;
  steps?: Record<string, number>;
  env: string | null;
  sample: number[];
  live_sample?: number[];
};

export type CreateJobPayload = {
  kind: 'bulk' | 'signal';
  action: string;
  params?: Record<string, any>;
  scope: JobScope;
  reason?: string;
  dry_run?: boolean;
  confirm_count?: number;
};

const unwrap = (raw: any) => (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw ?? {});
const BASE = '/api/admin/assets/jobs';
export const jobKeys = { all: ['asset-jobs'] as const };

const useGet = <T,>(msg: string) => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (url: string): Promise<T> => unwrap(await request(url)), msg);
};

export const useJobsQuery = (kind: string, status: string, page: number) => {
  const get = useGet<{ total: number; jobs: AssetJob[] }>('Could not load jobs');
  const qs = new URLSearchParams({ page: String(page), page_size: '25', ...(kind ? { kind } : {}), ...(status ? { status } : {}) });
  return useQuery({
    queryKey: [...jobKeys.all, 'list', kind, status, page],
    queryFn: () => get(`${BASE}?${qs}`),
    placeholderData: keepPreviousData,
    // Poll while anything on the page is still moving.
    refetchInterval: (q) => ((q.state.data as any)?.jobs ?? []).some((j: AssetJob) => j.status === 'queued' || j.status === 'running') ? 3000 : false,
  });
};

export const useJobQuery = (ajid: number | null) => {
  const get = useGet<AssetJob>('Could not load the job');
  return useQuery({
    queryKey: [...jobKeys.all, 'one', ajid],
    queryFn: () => get(`${BASE}/${ajid}`),
    enabled: ajid != null,
    refetchInterval: (q) => (['queued', 'running'].includes((q.state.data as any)?.status) ? 2000 : false),
  });
};

export const useJobItemsQuery = (ajid: number | null, status: string) => {
  const get = useGet<{ total: number; items: { asid: number; status: string; result: string | null }[] }>('Could not load job items');
  return useQuery({
    queryKey: [...jobKeys.all, 'items', ajid, status],
    queryFn: () => get(`${BASE}/${ajid}/items?page_size=1000${status ? `&status=${status}` : ''}`),
    enabled: ajid != null,
  });
};

/** POST /jobs; with dry_run it is a preview. Errors carry the plan in `data`. */
export const useCreateJobMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: CreateJobPayload) => unwrap(await request(BASE, 'POST', p)),
    onSuccess: (_d, p) => {
      if (!p.dry_run) {
        qc.invalidateQueries({ queryKey: jobKeys.all });
        qc.invalidateQueries({ queryKey: assetKeys.all });
      }
    },
  });
};

export const useJobControlMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async ({ ajid, verb, reason }: { ajid: number; verb: 'cancel' | 'retry-failed' | 'revert'; reason?: string }) =>
      unwrap(await request(`${BASE}/${ajid}/${verb}`, 'POST', reason ? { reason } : {})) as AssetJob,
    'The job could not be changed',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.all });
      qc.invalidateQueries({ queryKey: assetKeys.all });
      qc.invalidateQueries({ queryKey: ['asset-review'] });
    },
  });
};

export const ACTION_LABELS: Record<string, string> = {
  approve: 'Approve', hide: 'Hide', reject: 'Reject', restore: 'Restore', flag: 'Flag', unflag: 'Clear flag',
  delete: 'Delete', undelete: 'Undelete', run: 'Run analysis', unapprove: 'Undo approval', rehold: 'Re-apply decision',
};

/** Turn the Library's current query params into a job filter scope. */
export const filterScope = (params: Record<string, unknown>): Record<string, string> => {
  const out: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (['page', 'page_size', 'facets', 'sort'].includes(k)) return;
    if (v !== undefined && v !== null && v !== '') out[k] = String(v);
  });
  return out;
};
